import { useCallback, useEffect, useSyncExternalStore } from "react";
import { readAvatarVersion } from "../../api/avatarsApi";

/**
 * Which picture each person has, asked once per person and page load, so every avatar of
 * them shares one request. A person's own change is known at once; another person's
 * shows after the next load.
 */
type Known = { readonly status: "loading" } | { readonly status: "known"; readonly version: string | null };

const known = new Map<string, Known>();
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) {
    listener();
  }
}

function settle(userId: string, version: string | null): void {
  known.set(userId, { status: "known", version });
  announce();
}

function ask(userId: string): void {
  if (known.has(userId)) {
    return;
  }
  const loading: Known = { status: "loading" };
  known.set(userId, loading);
  // A change the person saved meanwhile is newer than this answer, so it wins.
  const stillAsked = () => known.get(userId) === loading;
  readAvatarVersion(userId).then(
    (version) => {
      if (stillAsked()) settle(userId, version);
    },
    () => {
      // Shown as initials; a later avatar of this person asks again.
      if (stillAsked()) known.delete(userId);
    },
  );
}

/** Record the signed-in person's new picture, or `null` once it is removed. */
export function rememberAvatarVersion(userId: string, version: string | null): void {
  settle(userId, version);
}

/** The version of the person's picture; `null` while unknown or when they have none. */
export function useAvatarVersion(userId: string | null): string | null {
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  const entry = useSyncExternalStore(subscribe, () => (userId === null ? undefined : known.get(userId)));
  useEffect(() => {
    if (userId !== null) {
      ask(userId);
    }
  }, [userId]);
  return entry?.status === "known" ? entry.version : null;
}
